import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Background, Controls, ReactFlow, type Edge, type Node } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useQuery } from '@tanstack/react-query'
import {
  getDeviceInstanceConnections,
  type DeviceInstanceDiagramConnection,
  type DeviceInstanceDiagramNode,
  type DeviceInstanceSchemaDiagram,
} from '../api/deviceInstances'
import { getCableToTerminalConnections, type CableToTerminalConnection } from '../api/connections'
import MainSchemaActuatorNode from './shared/nodes/MainSchemaActuatorNode'
import MainSchemaDeviceNode from './shared/nodes/MainSchemaDeviceNode'
import MainSchemaTerminalNode from './shared/nodes/MainSchemaTerminalNode'
import { EditableEdgeComponent } from '../edges/EditableEdge'
import { Algorithm } from '../edges/EditableEdge/constants'

interface DeviceInstanceUniversalDiagramProps {
  instanceId: number
  projectId: number
}

type WireProps = {
  type?: string
  color?: string
  stroke_width?: number
  stroke_dasharray?: string
  edge_labels?: Array<{ id: string; text: string; position: number }>
  edgeLabels?: Array<{ id: string; text: string; position: number }>
  style_properties?: Record<string, unknown>
}

const nodeTypes = {
  actuator: MainSchemaActuatorNode,
  device: MainSchemaDeviceNode,
  cable: MainSchemaDeviceNode,
  autoTerminal: MainSchemaTerminalNode,
}

const edgeTypes = {
  'editable-edge': EditableEdgeComponent,
}

function getAlgorithmFromWireProps(type?: string): Algorithm {
  switch (type) {
    case 'straight':
      return Algorithm.Linear
    case 'step':
      return Algorithm.Step
    case 'smoothstep':
      return Algorithm.CatmullRom
    case 'default':
    case 'bezier':
    default:
      return Algorithm.BezierCatmullRom
  }
}

function parseWireProps(connection: DeviceInstanceDiagramConnection | CableToTerminalConnection): WireProps {
  const raw = (connection as any).wire_properties
  if (!raw) return {}
  try {
    let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {}
  }
}

function parsePathPoints(connection: DeviceInstanceDiagramConnection | CableToTerminalConnection) {
  const raw = (connection as any).path_points
  if (!raw) return []
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!Array.isArray(parsed)) return []
    return parsed.map((point: any, index: number) => ({
      x: Number(point.x),
      y: Number(point.y),
      id: String(index),
    }))
  } catch (_error) {
    return []
  }
}

function nodeLabel(node: DeviceInstanceDiagramNode): string {
  return node.position_label || `${node.spec_name} #${node.instance_number}`
}

function getNodeType(node: DeviceInstanceDiagramNode): 'actuator' | 'device' | 'cable' {
  if (node.device_type_code === 'cable') return 'cable'
  if (node.device_type_code === 'actuator') return 'actuator'
  return 'device'
}

function makeRegularNode(
  node: DeviceInstanceDiagramNode,
  schemaId: number,
  position: { x: number; y: number },
  isFocused: boolean,
): Node {
  return {
    id: String(node.id),
    type: getNodeType(node),
    position,
    draggable: false,
    selectable: false,
    data: {
      deviceName: nodeLabel(node),
      deviceType: node.device_type_code,
      deviceTypeId: node.device_type_id,
      specName: node.spec_name,
      description: node.description || '',
      deviceInstanceId: node.id,
      specificationId: node.specification_id,
      contacts: node.contacts || [],
      layoutId: 0,
      isReserve: Boolean(node.is_reserve),
      knxAddress: node.knx_address || undefined,
      schemaId,
      disableAutoLoadContacts: true,
      className: isFocused ? 'ring-4 ring-blue-400' : undefined,
    },
  }
}

function makeEdge(
  id: string,
  source: string,
  target: string,
  sourceHandle: string,
  targetHandle: string,
  connection: DeviceInstanceDiagramConnection | CableToTerminalConnection,
): Edge {
  const wireProps = parseWireProps(connection)
  const pathPoints = parsePathPoints(connection)
  const edgeLabels = wireProps.edge_labels || wireProps.edgeLabels || []
  const edgeType = wireProps.type || 'bezier'
  const effectiveWireProps = {
    ...wireProps,
    type: edgeType,
  }

  return {
    id,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: 'editable-edge',
    animated: false,
    selectable: true,
    focusable: false,
    style: {
      stroke: effectiveWireProps.color || (effectiveWireProps.style_properties as any)?.stroke || '#007bff',
      strokeWidth: effectiveWireProps.stroke_width || (effectiveWireProps.style_properties as any)?.strokeWidth || 2,
      strokeDasharray: effectiveWireProps.stroke_dasharray || (effectiveWireProps.style_properties as any)?.strokeDasharray,
      ...(effectiveWireProps.style_properties || {}),
    },
    data: {
      connectionId: Number((connection as any).id ?? (connection as any).connection_id),
      points: pathPoints,
      algorithm: getAlgorithmFromWireProps(edgeType),
      edgeLabels,
      isReadOnly: true,
      deferLabelDragCommit: true,
      wireProperties: effectiveWireProps,
    },
  }
}

function buildFlowElements(
  schema: DeviceInstanceSchemaDiagram,
  instanceId: number,
  terminalConnections: CableToTerminalConnection[],
): { nodes: Node[]; edges: Edge[] } {
  const schemaId = schema.schema_id
  const regularNodes = schema.nodes.filter((node) => node.device_type_code !== 'terminal')
  const connectionIds = new Set(schema.connections.map((connection) => Number(connection.id)))
  const terminalHasOppositeConnection = (terminalConnection: CableToTerminalConnection) => {
    const terminalInstanceId = Number(terminalConnection.terminal_instance_id)
    const oppositeContactId = Number(terminalConnection.terminal_opposite_contact_id)
    if (!terminalInstanceId || !oppositeContactId) return false

    return schema.connections.some((connection) => {
      if (Number(connection.id) === Number(terminalConnection.connection_id)) return false
      return (
        (Number(connection.from_instance_id) === terminalInstanceId && Number(connection.from_contact_id) === oppositeContactId) ||
        (Number(connection.to_instance_id) === terminalInstanceId && Number(connection.to_contact_id) === oppositeContactId)
      )
    })
  }

  const relevantTerminalConnections = terminalConnections
    .filter((connection) => {
      return connectionIds.has(Number(connection.connection_id)) && terminalHasOppositeConnection(connection)
    })
    .sort((a, b) => {
      const blockCompare = String(a.terminal_block_name).localeCompare(String(b.terminal_block_name), 'ru')
      if (blockCompare !== 0) return blockCompare
      return Number(a.terminal_position_in_block) - Number(b.terminal_position_in_block)
    })

  const terminalNodeMap = new Map<string, { connection: CableToTerminalConnection; position: { x: number; y: number } }>()
  const terminalX = new Map<string, number>()

  const cableGroups = new Map<number, CableToTerminalConnection[]>()
  relevantTerminalConnections
    .filter((connection) => connection.cable_device_type === 'cable')
    .forEach((connection) => {
      const cableId = Number(connection.cable_instance_id)
      if (!cableGroups.has(cableId)) cableGroups.set(cableId, [])
      cableGroups.get(cableId)!.push(connection)
    })

  cableGroups.forEach((connections) => {
    if (connections.length === 0) return
    connections.sort((a, b) => {
      const aOrder = parseInt(String(a.cable_contact_number || '').split('-')[0]) || 0
      const bOrder = parseInt(String(b.cable_contact_number || '').split('-')[0]) || 0
      return aOrder - bOrder
    })

    const cableX = Number(connections[0].cable_position_x || 100)
    const cableY = Number(connections[0].cable_position_y || 100)
    const cableContactsCount = connections.length
    const cableWidth = cableContactsCount <= 4 ? 90 : Math.max(90, cableContactsCount * 18 + 20)
    const terminalGroupWidth = (connections.length - 1) * 40
    const startX = cableX + (cableWidth - terminalGroupWidth) / 2

    connections.forEach((connection, index) => {
      const nodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`
      const position = { x: startX + index * 40, y: cableY - 160 }
      terminalX.set(nodeId, position.x)
      if (!terminalNodeMap.has(nodeId)) terminalNodeMap.set(nodeId, { connection, position })
    })
  })

  relevantTerminalConnections
    .filter((connection) => connection.cable_device_type !== 'cable')
    .forEach((connection) => {
      const nodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`
      const hostNode = regularNodes.find((node) => node.id === Number(connection.cable_instance_id))
      const hostX = Number(hostNode?.layout_position_x ?? 100)
      const hostY = Number(hostNode?.layout_position_y ?? 100)
      const position = { x: hostX + 120, y: hostY + 160 }
      terminalX.set(nodeId, position.x)
      if (!terminalNodeMap.has(nodeId)) terminalNodeMap.set(nodeId, { connection, position })
    })

  const terminalRows = Array.from(terminalNodeMap.values())

  const nodes: Node[] = []
  const y = {
    up: -80,
    device: 120,
    terminals: 340,
    cables: 520,
    endDevices: 700,
  }

  const target = regularNodes.find((node) => node.id === instanceId)
  const upNodes = regularNodes.filter((node) => node.role === 'up')
  const neighborNodes = regularNodes.filter((node) => node.role === 'neighbor')
  const cableNodes = regularNodes.filter((node) => node.device_type_code === 'cable')
  const endNodes = regularNodes.filter((node) => node.role === 'down' && node.device_type_code !== 'cable')
  const otherNodes = regularNodes.filter((node) => {
    return node.id !== instanceId && node.role !== 'up' && node.role !== 'neighbor' && node.device_type_code !== 'cable' && node.role !== 'down'
  })

  const layoutPosition = (node: DeviceInstanceDiagramNode, fallback: { x: number; y: number }) => {
    if (node.layout_position_x !== null && node.layout_position_y !== null) {
      return { x: Number(node.layout_position_x), y: Number(node.layout_position_y) }
    }
    return fallback
  }

  upNodes.forEach((node, index) => nodes.push(makeRegularNode(node, schemaId, layoutPosition(node, { x: 300 + index * 210, y: y.up }), false)))
  neighborNodes.forEach((node, index) => nodes.push(makeRegularNode(node, schemaId, layoutPosition(node, { x: -250, y: y.device + index * 145 }), false)))
  if (target) nodes.push(makeRegularNode(target, schemaId, layoutPosition(target, { x: 300, y: y.device }), true))

  terminalRows.forEach(({ connection, position }) => {
    const nodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`
    nodes.push({
      id: nodeId,
      type: 'autoTerminal',
      position,
      draggable: false,
      selectable: false,
      data: connection as any,
    })
  })

  const cableX = new Map<number, number>()
  cableNodes.forEach((node, index) => {
    const connectedTerminals = relevantTerminalConnections
      .filter((connection) => connection.cable_device_type === 'cable' && Number(connection.cable_instance_id) === node.id)
      .map((connection) => terminalX.get(`terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`))
      .filter((value): value is number => typeof value === 'number')
    const fallbackX = connectedTerminals.length > 0
      ? connectedTerminals.reduce((sum, value) => sum + value, 0) / connectedTerminals.length - 28
      : 80 + index * 140
    const position = layoutPosition(node, { x: fallbackX, y: y.cables })
    cableX.set(node.id, position.x)
    nodes.push(makeRegularNode(node, schemaId, position, false))
  })

  endNodes.forEach((node, index) => {
    const connectedCable = schema.connections.find((connection) => {
      const fromIsCable = cableX.has(Number(connection.from_instance_id))
      const toIsCable = cableX.has(Number(connection.to_instance_id))
      return (
        (Number(connection.from_instance_id) === node.id && toIsCable) ||
        (Number(connection.to_instance_id) === node.id && fromIsCable)
      )
    })
    const cableId = connectedCable
      ? (cableX.has(Number(connectedCable.from_instance_id)) ? Number(connectedCable.from_instance_id) : Number(connectedCable.to_instance_id))
      : null
    const x = cableId && cableX.has(cableId) ? (cableX.get(cableId) || 0) - 5 : 80 + index * 140
    nodes.push(makeRegularNode(node, schemaId, layoutPosition(node, { x, y: y.endDevices }), false))
  })

  otherNodes.forEach((node, index) => nodes.push(makeRegularNode(node, schemaId, layoutPosition(node, { x: 80 + index * 160, y: y.endDevices + 160 }), false)))

  const nodeIds = new Set(nodes.map((node) => node.id))
  const autoConnectionIds = new Set(relevantTerminalConnections.map((connection) => Number(connection.connection_id)))
  const edges: Edge[] = []

  schema.connections.forEach((connection) => {
    const fromInstanceId = Number(connection.from_instance_id)
    const toInstanceId = Number(connection.to_instance_id)
    const fromContactId = Number(connection.from_contact_id)
    const toContactId = Number(connection.to_contact_id)
    if (!fromContactId || !toContactId) return

    const terminalConnection = relevantTerminalConnections.find((item) => Number(item.connection_id) === Number(connection.id))
    if (terminalConnection && terminalConnection.cable_device_type !== 'cable') {
      const terminalNodeId = `terminal-${terminalConnection.terminal_instance_id}-${terminalConnection.terminal_contact_id}`
      if (nodeIds.has(String(terminalConnection.cable_instance_id)) && nodeIds.has(terminalNodeId)) {
        edges.push(makeEdge(
          `device-terminal-${terminalConnection.connection_id}`,
          String(terminalConnection.cable_instance_id),
          terminalNodeId,
          `${terminalConnection.cable_contact_id}-source`,
          `${terminalConnection.terminal_opposite_contact_id || terminalConnection.terminal_contact_id}-top-target`,
          connection,
        ))
      }
      return
    }

    if (autoConnectionIds.has(Number(connection.id))) return
    if (!nodeIds.has(String(fromInstanceId)) || !nodeIds.has(String(toInstanceId))) return

    edges.push(makeEdge(
      `connection-${connection.id}`,
      String(fromInstanceId),
      String(toInstanceId),
      `${fromContactId}-source`,
      `${toContactId}-target`,
      connection,
    ))
  })

  relevantTerminalConnections
    .filter((connection) => connection.cable_device_type === 'cable')
    .forEach((connection) => {
      const terminalNodeId = `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`
      const cableNodeId = String(connection.cable_instance_id)
      if (!nodeIds.has(terminalNodeId) || !nodeIds.has(cableNodeId)) return
      edges.push(makeEdge(
        `auto-terminal-${connection.connection_id}`,
        terminalNodeId,
        cableNodeId,
        `${connection.terminal_contact_id}-bottom-source`,
        `${connection.cable_contact_id}-target`,
        connection,
      ))
    })

  return { nodes, edges }
}

export default function DeviceInstanceUniversalDiagram({ instanceId, projectId }: DeviceInstanceUniversalDiagramProps) {
  const [activeSchemaId, setActiveSchemaId] = useState<number | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['deviceInstanceConnections', instanceId],
    queryFn: () => getDeviceInstanceConnections(instanceId),
    enabled: !!instanceId,
  })

  const schemas = useMemo(() => {
    return (data?.schemas || []).filter((schema) => schema.schema_type === 'main' || schema.schema_type === 'universal')
  }, [data])

  const activeSchema = useMemo(() => {
    if (schemas.length === 0) return null
    if (activeSchemaId === null) return schemas[0]
    return schemas.find((schema) => schema.schema_id === activeSchemaId) || schemas[0]
  }, [schemas, activeSchemaId])

  useEffect(() => {
    if (schemas.length > 0 && activeSchemaId === null) {
      setActiveSchemaId(schemas[0].schema_id)
    }
  }, [schemas, activeSchemaId])

  const { data: terminalConnections = [] } = useQuery({
    queryKey: ['deviceInstanceUniversalTerminalConnections', activeSchema?.schema_id],
    queryFn: () => getCableToTerminalConnections(Number(activeSchema!.schema_id)),
    enabled: !!activeSchema?.schema_id,
  })

  const flow = useMemo(() => {
    if (!activeSchema) return { nodes: [], edges: [] }
    return buildFlowElements(activeSchema as any, instanceId, terminalConnections)
  }, [activeSchema, instanceId, terminalConnections])

  const viewport = useMemo(() => {
    const focusedNode = flow.nodes.find((node) => node.id === String(instanceId)) || flow.nodes[0]
    if (!focusedNode) return { x: 160, y: 70, zoom: 0.72 }
    const zoom = 0.75
    return {
      x: 360 - focusedNode.position.x * zoom,
      y: 180 - focusedNode.position.y * zoom,
      zoom,
    }
  }, [flow.nodes, instanceId])

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
        <div className="h-[520px] bg-gray-50 rounded" />
      </div>
    )
  }

  if (error || schemas.length === 0) {
    return null
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 space-y-4">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Схемы соединений контактов</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            ReactFlow-предпросмотр universal/main схемы: контакты, клеммы, кабели и линии соединений
          </p>
        </div>
        {activeSchema && (
          <Link
            to={`/projects/${projectId}/schemas/${activeSchema.schema_id}?focus=${instanceId}`}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Открыть полную схему
          </Link>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {schemas.map((schema) => (
          <button
            key={schema.schema_id}
            onClick={() => setActiveSchemaId(schema.schema_id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeSchema?.schema_id === schema.schema_id
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {schema.schema_name}
          </button>
        ))}
      </div>

      <div className="h-[720px] overflow-hidden rounded-xl border border-gray-200 bg-slate-50">
        {activeSchema && flow.nodes.length > 0 ? (
          <ReactFlow
            key={`${activeSchema.schema_id}-${flow.nodes.length}-${flow.edges.length}`}
            nodes={flow.nodes}
            edges={flow.edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            defaultViewport={viewport}
            onInit={(instance) => {
              window.setTimeout(() => instance.setViewport(viewport), 250)
              window.setTimeout(() => instance.setViewport(viewport), 900)
            }}
            minZoom={0.08}
            maxZoom={1.4}
          >
            <Background gap={16} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            Для этой схемы не удалось построить подграф соединений.
          </div>
        )}
      </div>
    </div>
  )
}
